<script setup>
  import { ref } from 'vue'

  const email = ref('')
  const consentNewsletter = ref(false)
  const loading = ref(false)
  const message = ref('')
  const isSuccess = ref(false)

  // Mailchimp configuration
  const config = useRuntimeConfig()
  const { server, listId, audienceId, domain, fId, gdprId } = config.public.mailchimp

const ERROR_MESSAGES = {
  SUBSCRIPTION_FAILED: 'An error occurred. Please try again later.',
  NETWORK_ERROR: 'An error occurred. Please try again later.'
}

  // Helper function to strip HTML tags safely
  const stripHtmlTags = (html) => {
    if (!html) return ''
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('div')
    tmp.textContent = html
    return tmp.textContent || tmp.innerText || ''
  }

  const handleNewsletterSubmit = async () => {
    if (!email.value || !consentNewsletter.value) return

    loading.value = true
    message.value = ''
    isSuccess.value = false

    const callbackName = 'mcCallback' + Date.now()
    const url = `https://${domain}.${server}.list-manage.com/subscribe/post-json?u=${audienceId}&id=${listId}&f_id=${fId}&EMAIL=${encodeURIComponent(email.value)}&gdpr[${gdprId}]=Y&c=${callbackName}`

    const script = document.createElement('script')
    script.src = url

    window[callbackName] = (response) => {
      if (response?.result === 'success') {
        isSuccess.value = true
        message.value = stripHtmlTags(response?.msg)
        email.value = ''
        consentNewsletter.value = false
      } else {
        isSuccess.value = false
        message.value = stripHtmlTags(response?.msg) || ERROR_MESSAGES.SUBSCRIPTION_FAILED
      }

      loading.value = false
      cleanup()
    }

    // Cleanup function
    const cleanup = () => {
      delete window[callbackName]
      document.body.removeChild(script)
    }

    script.onerror = () => {
      loading.value = false
      isSuccess.value = false
      message.value = ERROR_MESSAGES.NETWORK_ERROR
      cleanup()
    }

    document.body.appendChild(script)
  }
</script>

<template>
  <div class="space-y-4 lg:col-span-5 xl:col-span-2">
    <div class="space-y-3 mt-12">
      <div class="inline-flex items-center gap-2 rounded-xl bg-sky-500/20 border border-sky-400/30 px-3 py-1">
        <div class="w-1.5 h-1.5 bg-sky-400 rounded-full animate-pulse"></div>
        <span class="text-xs font-semibold text-sky-300 uppercase tracking-wider">
          {{ $t('footer.newsletter') }}
        </span>
      </div>

      <label class="flex items-start space-x-2.5 p-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 cursor-pointer">
        <input
          v-model="consentNewsletter"
          type="checkbox"
          class="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/10 text-sky-500 focus:ring-2 focus:ring-sky-400/50 focus:ring-offset-0 transition-all cursor-pointer"
        />
        <span class="text-xs text-slate-300 leading-relaxed flex-1">
          {{ $t('footer.consent') }}
        </span>
      </label>

      <div class="flex gap-2">
        <input
          v-model="email"
          type="email"
          placeholder="Email"
          class="flex-1 px-3 py-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 focus:border-transparent transition-all"
          :disabled="loading"
        />
        <button
          :disabled="!email || !consentNewsletter || loading"
          :aria-label="loading ? $t('common.loading') : $t('footer.subscribe')"
          class="group px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-slate-700 cursor-pointer disabled:cursor-not-allowed rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-sky-500/20 flex items-center justify-center min-w-[3rem]"
          @click="handleNewsletterSubmit"
        >
          <font-awesome
            v-if="!loading"
            :icon="['fas', 'arrow-right']"
            class="text-white transition-transform duration-300 group-hover:translate-x-1"
          />
          <font-awesome v-else :icon="['fas', 'spinner']" spin class="text-white" />
        </button>
      </div>

      <!-- Feedback Message -->
      <div v-if="message" class="text-xs px-1" :class="isSuccess ? 'text-green-400' : 'text-red-400'">
        {{ message }}
      </div>
    </div>
  </div>
</template>
